import * as React from "react";
import { Genome } from "../../../api";
import { depthFormatter, percentageFormatter } from "../../../utils/metadata-formatter";
import { getGenomeStats } from "../../../utils/qc-statistics";
import CircularProgressBar, { ProgressBarNumbers } from "../../circular-progress-bar";
import { InfoTooltip } from "../../info-tooltip";
import {
  QualityControlSection,
  QualityControlStatuses,
  QualityControlStatusLine,
  QualityControlWrapper,
  QualityStatus,
} from "../../quality-control";

class MicrobeDetailsQualityControl extends React.PureComponent<{
  genome: Genome;
  collectionName: string | null;
  notes: string | null;
}> {
  usesBuscoQC() {
    return !!this.props.collectionName && this.props.collectionName === "mycology";
  }

  usesCheckMQC() {
    return !!this.props.collectionName && this.props.collectionName === "bacteriology";
  }

  render() {
    return (
      <QualityControlWrapper>
        <QualityControlSection
          header="Sequencing Quality Control"
          description="Quality control statistics on Illumina sequencing data."
        >
          <IlluminaQualityStats
            genome={this.props.genome}
            isViral={this.props.collectionName === "virology"}
          />
        </QualityControlSection>
        {this.usesCheckMQC() && (
          <QualityControlSection
            header="Assembly Quality Control"
            description={
              <>
                Metrics assessing the assembly quality
                <InfoTooltip>
                  CheckM assesses genome quality metrics including completeness and contamination.
                  See Parks 2015
                </InfoTooltip>
              </>
            }
          >
            <AssemblyQualityStatus genome={this.props.genome} />
          </QualityControlSection>
        )}
        {this.usesBuscoQC() && (
          <QualityControlSection
            header="BUSCO Quality Control"
            description="Metrics assessing the assembly quality (from BUSCO)."
          >
            <FungalQualityStats genome={this.props.genome} />
          </QualityControlSection>
        )}
        {this.props.notes && (
          <QualityControlSection header="Notes">
            <p>{this.props.notes}</p>
          </QualityControlSection>
        )}
      </QualityControlWrapper>
    );
  }
}

const AssemblyQualityStatus = ({ genome }: { genome: Genome }) => {
  const genomeStats = getGenomeStats(genome);
  if (genomeStats?.qualityControl.assembly === undefined) {
    return null;
  }

  const { assembly } = genomeStats.qualityControl;
  const { contamination, completeness } = assembly;

  // NOTE - contamination and completeness currently being returned as numerical representation of stringified percentages: 100.0 vs. 1.0
  //
  const statuses = [
    getQCItemStatus(assembly, "completeness", [90, 85]),
    getQCItemStatus(assembly, "contamination", [10, 15]),
    getQCItemStatus(assembly, "depth", [100, 50]),
  ];

  const rows = (
    <>
      {completeness !== null && (
        <QualityControlStatusLine
          name="Estimated genome completeness"
          status={statuses[0]}
          value={formatQCItemValue(assembly, "completeness", percentValueToString)}
        />
      )}
      {contamination !== null && (
        <QualityControlStatusLine
          name="Estimated genome contamination"
          status={statuses[1]}
          value={formatQCItemValue(assembly, "contamination", percentValueToString)}
        />
      )}
      <QualityControlStatusLine
        name="Average depth of coverage"
        status={statuses[2]}
        value={formatQCItemValue(assembly, "depth", depthFormatter)}
      />
    </>
  );

  return (
    <>
      <CircularProgressBar numbers={statusReportsToProgressBarNumbers(statuses)} />
      <QualityControlStatuses>{rows}</QualityControlStatuses>
    </>
  );
};

const IlluminaQualityStats = ({ genome, isViral }: { genome: Genome; isViral: boolean }) => {
  const genomeStats = getGenomeStats(genome);
  if (genomeStats === null) {
    return null;
  }

  const { sequencing } = genomeStats.qualityControl;
  const { medianQScorePercent, ambiguousContent } = sequencing;

  const statuses: QualityStatus[] = [];
  if (!isViral) {
    statuses.push(getQCItemStatus(sequencing, "trimmedReads", [1000000, 500000]));
  }
  statuses.push(
    ...[
      getQCItemStatus(sequencing, "medianQScore", [30, 30]),
      getQCItemStatus(sequencing, "medianQScorePercent", [1.0, 1.0]),
      getQCItemStatus(sequencing, "ambiguousContent", [5, 10]),
    ]
  );

  const rows = (
    <>
      {!isViral && (
        <QualityControlStatusLine
          name="Number of trimmed reads"
          status={statuses[0]}
          value={formatQCItemValue(sequencing, "trimmedReads")}
        />
      )}
      <QualityControlStatusLine
        name="Median Q score, all bases"
        status={statuses[1]}
        value={formatQCItemValue(sequencing, "medianQScore")}
      />
      {medianQScorePercent !== null && (
        <QualityControlStatusLine
          name="Percent of median Q scores per cycle greater than 25"
          status={statuses[2]}
          value={formatQCItemValue(sequencing, "medianQScorePercent", percentValueToString)}
        />
      )}
      {ambiguousContent !== null && (
        <QualityControlStatusLine
          name="Ambiguous content (% N bases)"
          status={statuses[3]}
          value={formatQCItemValue(sequencing, "ambiguousContent", percentValueToString)}
        />
      )}
    </>
  );

  return (
    <>
      <CircularProgressBar numbers={statusReportsToProgressBarNumbers(statuses)} />
      <QualityControlStatuses>{rows}</QualityControlStatuses>
    </>
  );
};

const FungalQualityStats = ({ genome }: { genome: Genome }) => {
  const genomeStats = getGenomeStats(genome);
  let depthMean: number | null = null;

  if (genomeStats?.sequencingStats?.illumina?.depth?.mean !== undefined) {
    depthMean = genomeStats.sequencingStats.illumina.depth.mean || 0;
  }
  const completeness = genomeStats?.qualityControl?.assembly?.completeness;

  const values = {
    depthMean,
    completeness,
  };

  if (!completeness) {
    return null;
  }

  const statuses = [
    getQCItemStatus(values, "completeness", [80, 40]),
    getQCItemStatus(values, "depthMean", [100, 50]),
  ];

  const rows = (
    <>
      <QualityControlStatusLine
        name="Estimated genome completeness"
        status={statuses[0]}
        value={formatQCItemValue(values, "completeness", percentValueToString)}
      />
      <QualityControlStatusLine
        name="Average depth of coverage"
        status={statuses[1]}
        value={formatQCItemValue(values, "depthMean", depthFormatter)}
      />
    </>
  );

  return (
    <>
      <CircularProgressBar numbers={statusReportsToProgressBarNumbers(statuses)} />
      <QualityControlStatuses>{rows}</QualityControlStatuses>
    </>
  );
};

// percentageFormatter expects a value to be a ratio [0, 1.0].
// This expects it to be a percent value [0.0, 100.0].
function percentValueToString(value: number): string {
  return percentageFormatter(value / 100.0);
}

export type OnlyNumberFields = Record<string, number | undefined | null>;

export function formatQCItemValue<T extends OnlyNumberFields>(
  obj: T | undefined,
  field: keyof T,
  valueFormatter?: (value: number) => string
): string {
  if (!obj) {
    return "-";
  }
  const value = obj[field];
  if (value === null || value === undefined) {
    return "-";
  }

  if (valueFormatter) {
    return valueFormatter(value);
  }
  return value.toLocaleString();
}

// NOTE - these cutoffs for PASSED, CAUTION, and FAILED are magic numbers based on conversations with Austin
export function getQCItemStatus<T extends OnlyNumberFields>(
  obj: T | undefined,
  field: keyof T,
  bounds: [number, number]
): QualityStatus {
  if (!obj) {
    return QualityStatus.Missing;
  }
  const value = obj[field];
  if (value === null || value === undefined) {
    return QualityStatus.Missing;
  }

  const [b0, b1] = bounds;
  const lowerIsBetter = b0 < b1;

  if (lowerIsBetter) {
    if (value <= b0) {
      return QualityStatus.Passed;
    }
    if (value <= b1) {
      return QualityStatus.Caution;
    }
    return QualityStatus.Failed;
  }

  if (value >= b0) {
    return QualityStatus.Passed;
  }
  if (value >= b1) {
    return QualityStatus.Caution;
  }

  return QualityStatus.Failed;
}

function statusReportsToProgressBarNumbers(statuses: QualityStatus[]): ProgressBarNumbers {
  const result: ProgressBarNumbers = {
    numPassed: statuses.filter((s) => s === QualityStatus.Passed).length,
    numFailed: statuses.filter((s) => s === QualityStatus.Failed).length,
    numCaution: statuses.filter((s) => s === QualityStatus.Caution).length,
    numChecked: statuses.filter((s) => s !== QualityStatus.Missing).length,
  };
  return result;
}

export default MicrobeDetailsQualityControl;
